import type { PtoPprTkDraft } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Loader, Progress } from '@shared/ui/Progress';
import { Muted, Tag } from '../kfo/ui';
import { RdDraftView } from './RdDraftView';
import {
  STALL_TEXT,
  docShort,
  isRdBusy,
  isRdStalled,
  progressLabel,
  progressPercent,
  reviewBadge,
} from './model';
import {
  DraftBody,
  DraftHead,
  DraftName,
  DraftRow,
  ErrorText,
  ProgressWrap,
  StatusLine,
} from './ui';
import { useNow } from './useNow';
import { CONFIRM_RESTART_STALLED, useRdActions, type RdActions } from './useRdActions';

/**
 * Статус черновика rd в списке: ход генерации, ошибка или итог аудита. Список
 * отдаёт черновики rd без разделов и паспорта РД — здесь только статусы.
 */
function RdStatus({
  draft,
  stalled,
  canWrite,
  actions,
}: {
  draft: PtoPprTkDraft;
  stalled: boolean;
  canWrite: boolean;
  actions: RdActions;
}) {
  if (isRdBusy(draft)) {
    const percent = progressPercent(draft);
    return (
      <StatusLine data-ppr-status={stalled ? 'stalled' : 'busy'}>
        {!stalled && <Loader />}
        <span>{progressLabel(draft)}</span>
        {percent != null && (
          <ProgressWrap>
            <Progress percent={percent} />
          </ProgressWrap>
        )}
        {stalled && (
          <>
            <ErrorText>{STALL_TEXT}</ErrorText>
            {canWrite && (
              <Button
                $variant="secondary"
                $size="sm"
                type="button"
                disabled={actions.restarting}
                onClick={() => actions.restart(CONFIRM_RESTART_STALLED)}
              >
                Перезапустить
              </Button>
            )}
          </>
        )}
      </StatusLine>
    );
  }
  if (draft.gen_status === 'failed') {
    return (
      <StatusLine data-ppr-status="failed">
        <ErrorText>Ошибка: {draft.gen_error || 'генерация не завершилась'}</ErrorText>
        {canWrite && (
          <Button
            $variant="secondary"
            $size="sm"
            type="button"
            disabled={actions.restarting}
            onClick={() => actions.restart(null)}
          >
            Перезапустить
          </Button>
        )}
      </StatusLine>
    );
  }
  const badge = reviewBadge(draft.review);
  const recheck = draft.review?.status === 'failed' || draft.review?.status === 'stale';
  return (
    <StatusLine data-ppr-status={draft.review?.status ?? 'none'}>
      {badge && <Tag $tone={badge.tone}>{badge.text}</Tag>}
      {canWrite && recheck && (
        <Button
          $variant="secondary"
          $size="sm"
          type="button"
          disabled={actions.reviewing}
          onClick={actions.review}
        >
          Проверить заново
        </Button>
      )}
    </StatusLine>
  );
}

/** Черновик режима rd в списке черновиков ППР/ТК: строка со статусом + раскрытие. */
export function RdDraftRow({
  draft,
  open,
  onToggle,
  canWrite,
  onRefresh,
}: {
  /** Черновик из списка: без разделов и паспорта РД (их отдаёт GET черновика). */
  draft: PtoPprTkDraft;
  open: boolean;
  onToggle: () => void;
  canWrite: boolean;
  /** Перечитать список черновиков. */
  onRefresh: () => PromiseLike<unknown>;
}) {
  const actions = useRdActions(draft);
  const busy = isRdBusy(draft);
  // Часы тикают, пока черновик занят: «зависла» появится и без новых данных.
  const now = useNow(busy);
  const stalled = isRdStalled(draft, now);
  return (
    <DraftRow data-draft={draft.id} data-mode="rd">
      <DraftHead>
        <DraftName>
          <strong>{docShort(draft.kind)}</strong> <Tag $tone="info">по РД</Tag> · {draft.title}{' '}
          <Muted as="span">({draft.work_type})</Muted>
        </DraftName>
        <RdStatus draft={draft} stalled={stalled} canWrite={canWrite} actions={actions} />
        <Button $variant="ghost" $size="sm" type="button" onClick={onToggle}>
          {open ? 'Свернуть' : busy ? 'Ход работы' : 'Открыть'}
        </Button>
        {canWrite && (
          <Button
            $variant="ghost"
            $size="sm"
            type="button"
            title="Удалить черновик"
            disabled={actions.deleting}
            onClick={actions.remove}
          >
            ✕
          </Button>
        )}
      </DraftHead>
      {!open && actions.error && <ErrorText>{actions.error}</ErrorText>}
      {open && (
        <DraftBody>
          <RdDraftView
            listDraft={draft}
            canWrite={canWrite}
            actions={actions}
            now={now}
            onRefreshList={onRefresh}
          />
        </DraftBody>
      )}
    </DraftRow>
  );
}
